import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { PdfDocumentMetrics, PdfFieldRect } from "./types";
import type { TextMeasurer, TextLine } from "./textLayout";
import { calculateOptimalLayout } from "./textLayout";

interface PdfFieldOverlayProps {
  fields: PdfFieldRect[];
  metrics: PdfDocumentMetrics;
  selectedId?: string | null;
  onSelect?: (field: PdfFieldRect) => void;
  onUpdateField?: (fieldId: string, updates: { x: number; y: number }) => void;
  draggingEnabled?: boolean;
  className?: string;
  renderLabel?: (field: PdfFieldRect) => ReactNode;
  highlightClassName?: string;
}

interface FieldMeta {
  label?: string;
  value?: string;
  fontSize?: number;
}

function extractMeta(field: PdfFieldRect): FieldMeta {
  const meta = field.meta as FieldMeta | undefined;
  return {
    label: meta?.label,
    value: meta?.value,
    fontSize: meta?.fontSize,
  };
}

function createDomTextMeasurer(fontFamily = "Inter, sans-serif", fontWeight = "500"): TextMeasurer | null {
  if (typeof document === "undefined") {
    return null;
  }
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  return {
    widthOfText: (text, fontSize) => {
      ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
      return ctx.measureText(text).width;
    },
    heightAtSize: (fontSize) => fontSize,
  };
}

const fallbackMeasurer: TextMeasurer = {
  widthOfText: (text, fontSize) => text.length * fontSize * 0.6,
  heightAtSize: (fontSize) => fontSize,
};

export function PdfFieldOverlay({
  fields,
  metrics,
  selectedId,
  onSelect,
  onUpdateField,
  draggingEnabled = true,
  className,
  renderLabel,
  highlightClassName,
}: PdfFieldOverlayProps) {
  const displayWidth = metrics.viewport.width * metrics.viewport.scale;
  const displayHeight = metrics.viewport.height * metrics.viewport.scale;
  const measurer = useMemo(
    () => createDomTextMeasurer() ?? fallbackMeasurer,
    [],
  );

  const [draggingFieldId, setDraggingFieldId] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
  const [hasMoved, setHasMoved] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const draggingFieldIdRef = useRef<string | null>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const hasMovedRef = useRef(false);

  // Sync refs with state
  useEffect(() => {
    dragOffsetRef.current = dragOffset;
    draggingFieldIdRef.current = draggingFieldId;
    dragStartRef.current = dragStart;
    hasMovedRef.current = hasMoved;
  }, [dragOffset, draggingFieldId, dragStart, hasMoved]);

  // Convert display coordinates to normalized coordinates
  const displayToNormalized = useCallback(
    (displayX: number, displayY: number, fieldWidth: number, fieldHeight: number) => {
      const x = displayX / displayWidth;
      // Convert from top-left display coordinates to normalized coordinates
      // displayY is from top, but normalized y is from bottom
      const normalizedTop = displayY / displayHeight;
      const y = 1 - normalizedTop - (fieldHeight / displayHeight);
      return { x, y };
    },
    [displayWidth, displayHeight],
  );

  // Convert normalized coordinates to display coordinates
  const normalizedToDisplay = useCallback(
    (x: number, y: number, width: number, height: number) => {
      const left = x * displayWidth;
      const top = displayHeight - (y * displayHeight) - height;
      return { left, top };
    },
    [displayWidth, displayHeight],
  );

  // Clamp a value between min and max
  const clamp = useCallback((min: number, value: number, max: number) => {
    return Math.max(min, Math.min(max, value));
  }, []);

  // Handle pointer down to start drag
  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>, field: PdfFieldRect) => {
      if (!draggingEnabled || !onUpdateField) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const overlay = overlayRef.current;
      if (!overlay) return;

      const bounds = overlay.getBoundingClientRect();
      const point = {
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      };

      const fieldWidth = field.rect.width * displayWidth;
      const fieldHeight = field.rect.height * displayHeight;
      const fieldLeft = field.rect.x * displayWidth;
      const fieldTop = displayHeight - (field.rect.y * displayHeight) - fieldHeight;

      // Check if click is within field bounds
      if (
        point.x >= fieldLeft &&
        point.x <= fieldLeft + fieldWidth &&
        point.y >= fieldTop &&
        point.y <= fieldTop + fieldHeight
      ) {
        setDraggingFieldId(field.id);
        setDragStart(point);
        setDragOffset({ x: 0, y: 0 });
        setHasMoved(false);
        (event.target as HTMLElement).setPointerCapture(event.pointerId);
      }
    },
    [draggingEnabled, onUpdateField, displayWidth, displayHeight],
  );

  // Handle pointer up to end drag
  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingFieldId || !onUpdateField) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const field = fields.find((f) => f.id === draggingFieldId);
      if (field && dragOffset) {
        const newX = clamp(0, field.rect.x + dragOffset.x, 1 - field.rect.width);
        const newY = clamp(0, field.rect.y + dragOffset.y, 1 - field.rect.height);

        onUpdateField(draggingFieldId, { x: newX, y: newY });
      }

      setDraggingFieldId(null);
      setDragStart(null);
      setDragOffset(null);
    },
    [draggingFieldId, dragOffset, fields, clamp, onUpdateField],
  );

  // Handle pointer cancel
  const handlePointerCancel = useCallback(() => {
    setDraggingFieldId(null);
    setDragStart(null);
    setDragOffset(null);
  }, []);

  // Global pointer move handler for drag
  useEffect(() => {
    if (!draggingFieldId) return;

    const handleGlobalPointerMove = (event: PointerEvent) => {
      const currentDraggingId = draggingFieldIdRef.current;
      const currentDragStart = dragStartRef.current;
      
      if (!currentDraggingId || !currentDragStart || !onUpdateField) {
        return;
      }

      const overlay = overlayRef.current;
      if (!overlay) return;

      const bounds = overlay.getBoundingClientRect();
      const currentPoint = {
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      };

      const field = fields.find((f) => f.id === currentDraggingId);
      if (!field) return;

      const fieldWidth = field.rect.width * displayWidth;
      const fieldHeight = field.rect.height * displayHeight;

      const deltaX = currentPoint.x - currentDragStart.x;
      const deltaY = currentPoint.y - currentDragStart.y;

      const fieldLeft = field.rect.x * displayWidth;
      const fieldTop = displayHeight - (field.rect.y * displayHeight) - fieldHeight;

      let newLeft = fieldLeft + deltaX;
      let newTop = fieldTop + deltaY;

      newLeft = clamp(0, newLeft, displayWidth - fieldWidth);
      newTop = clamp(0, newTop, displayHeight - fieldHeight);

      const normalized = displayToNormalized(newLeft, newTop, fieldWidth, fieldHeight);
      const clampedX = clamp(0, normalized.x, 1 - field.rect.width);
      const clampedY = clamp(0, normalized.y, 1 - field.rect.height);

      const newOffset = {
        x: clampedX - field.rect.x,
        y: clampedY - field.rect.y,
      };
      
      // Check if we've moved enough to consider it a drag (threshold of 3px)
      const moveThreshold = 3;
      const moved = Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold;
      if (moved) {
        setHasMoved(true);
      }
      
      dragOffsetRef.current = newOffset;
      setDragOffset(newOffset);
    };

    const handleGlobalPointerUp = () => {
      const currentDraggingId = draggingFieldIdRef.current;
      const currentDragOffset = dragOffsetRef.current;
      const moved = hasMovedRef.current;
      
      if (!currentDraggingId || !onUpdateField) {
        return;
      }

      const field = fields.find((f) => f.id === currentDraggingId);
      // Only update if we actually moved (dragged), not just clicked
      if (field && currentDragOffset && moved) {
        const newX = clamp(0, field.rect.x + currentDragOffset.x, 1 - field.rect.width);
        const newY = clamp(0, field.rect.y + currentDragOffset.y, 1 - field.rect.height);

        onUpdateField(currentDraggingId, { x: newX, y: newY });
      }

      setDraggingFieldId(null);
      setDragStart(null);
      setDragOffset(null);
      setHasMoved(false);
      dragOffsetRef.current = null;
      draggingFieldIdRef.current = null;
      dragStartRef.current = null;
      hasMovedRef.current = false;
    };

    window.addEventListener("pointermove", handleGlobalPointerMove);
    window.addEventListener("pointerup", handleGlobalPointerUp);

    return () => {
      window.removeEventListener("pointermove", handleGlobalPointerMove);
      window.removeEventListener("pointerup", handleGlobalPointerUp);
    };
  }, [draggingFieldId, fields, displayWidth, displayHeight, displayToNormalized, clamp, onUpdateField]);

  return (
    <div
      ref={overlayRef}
      className={cn("pointer-events-none absolute inset-0", className)}
      style={{ width: displayWidth, height: displayHeight, zIndex: 10 }}
    >
      {fields.map((field) => {
        const { rect } = field;
        const width = rect.width * displayWidth;
        const height = rect.height * displayHeight;
        
        // Apply drag offset if this field is being dragged
        const dragX = draggingFieldId === field.id && dragOffset ? dragOffset.x : 0;
        const dragY = draggingFieldId === field.id && dragOffset ? dragOffset.y : 0;
        
        const baseLeft = rect.x * displayWidth;
        const baseTop = displayHeight - (rect.y * displayHeight) - height;
        
        const left = baseLeft + (dragX * displayWidth);
        const top = baseTop - (dragY * displayHeight);
        
        const isSelected = selectedId === field.id;
        const isDragging = draggingFieldId === field.id;
        const meta = extractMeta(field);
        const value = typeof meta.value === "string" && meta.value.length > 0 ? meta.value : null;
        const hasValue = !!value;
        const lineHeightMultiplier = 1.1;

        let valueLines: TextLine[] = [];
        let valueFontSize = 0;
        let valueLineHeight = 0;
        if (hasValue) {
          // Calculate minimum font size that scales with zoom
          // Base minimum is 6px, scaled by viewport.scale (which includes zoom)
          const minFontSize = 6 * metrics.viewport.scale;
          
          const baseFontSize =
            typeof meta.fontSize === "number" && meta.fontSize > 0
              ? meta.fontSize * metrics.viewport.scale
              : Math.max(Math.round(height * 0.45), minFontSize);

          const layout = calculateOptimalLayout({
            text: value!,
            measurer,
            initialFontSize: baseFontSize,
            containerWidth: width,
            containerHeight: height,
            containerY: 0,
            minFontSize,
            lineHeightMultiplier,
          });

          valueLines = layout.lines;
          valueFontSize = layout.fontSize;
          valueLineHeight = layout.lineHeight;
        }

        return (
          <div
            key={field.id}
            data-field-overlay
            style={{
              width,
              height,
              left,
              top,
              cursor: draggingEnabled && onUpdateField ? "move" : onSelect ? "pointer" : "default",
              opacity: isDragging ? 0.8 : 1,
              zIndex: isDragging ? 50 : 20,
            }}
            role={onSelect ? "button" : undefined}
            tabIndex={onSelect ? 0 : undefined}
            onPointerDown={
              draggingEnabled && onUpdateField
                ? (event) => {
                    event.stopPropagation(); // Prevent PdfFieldCanvas from handling this
                    handlePointerDown(event, field);
                  }
                : undefined
            }
            onPointerCancel={draggingEnabled && onUpdateField ? handlePointerCancel : undefined}
            onClick={
              onSelect
                ? (event) => {
                    // Only select if we didn't drag (moved less than threshold)
                    if (!hasMovedRef.current) {
                      event.stopPropagation();
                      onSelect(field);
                    }
                  }
                : undefined
            }
            className={cn(
              "absolute border border-red-500/70 bg-red-500/10",
              isSelected && "border-blue-500/80 bg-blue-500/10",
              highlightClassName,
              (onSelect || (draggingEnabled && onUpdateField)) ? "pointer-events-auto" : "pointer-events-none",
            )}
          >
            {hasValue ? (
              <div
                className="pointer-events-none absolute inset-0 overflow-hidden"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  color: "#1d4ed8",
                  gap: 0,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: "0 4px",
                    fontSize: valueFontSize,
                    lineHeight: `${valueLineHeight}px`,
                    fontWeight: 500,
                    fontFamily: "Inter, sans-serif",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {valueLines.map((line, index) => (
                    <div key={`${field.id}-line-${index}`} className="leading-[inherit]">
                      {line.text}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {!hasValue ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-1 text-center">
                {renderLabel ? (
                  renderLabel(field)
                ) : meta.label ? (
                  <span
                    className="font-medium text-blue-600 leading-none"
                    style={{ fontSize: 10 * metrics.viewport.scale }}
                  >
                    {meta.label}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

